/**
 * The seats this device holds in live games, so you can come back to them.
 *
 * A rejoin token is what makes a chair yours: present it and you get the same
 * seat and the same cards back. It lives in `localStorage` because phones close
 * tabs and kill backgrounded pages, and losing your seat because the screen
 * locked mid-round would be the single most annoying possible bug.
 *
 * **A list, not one slot.** All three games shared a single key, so joining a
 * hartenjagen table quietly overwrote your boerenbridge seat — and once that
 * game had started there was no way back into it: a fresh join is a new seat,
 * and a started game refuses one. That is the bug this module exists to fix.
 *
 * Nothing here talks to the network. Whether a room still exists is the server's
 * answer, and `Home` asks it.
 */

const STORAGE_KEY = 'boerenbridge.live.seats';
/** The single-slot key this replaces. Read once, then folded in. */
const LEGACY_KEY = 'boerenbridge.live.session';

/** Which game a code belongs to, so a saved seat knows where to send you. */
export type SeatMode = 'live' | 'hearts' | 'klaverjas';

export interface SavedSeat {
  code: string;
  mode: SeatMode;
  /** The name this seat plays under. Empty at a klaverjas table, which names its own. */
  name: string;
  rejoinToken: string;
  /** ISO, so the newest potje comes first without a second field to keep in step. */
  savedAt: string;
}

/** Where tapping this seat takes you. One place, so the three routes cannot drift. */
export function routeForSeat(seat: SavedSeat): string {
  switch (seat.mode) {
    case 'hearts':
      return `/harten/${seat.code}`;
    case 'klaverjas':
      return `/klaverjas/live/${seat.code}`;
    default:
      return `/live/${seat.code}`;
  }
}

function isSeat(value: unknown): value is SavedSeat {
  if (typeof value !== 'object' || value === null) return false;
  const seat = value as Partial<SavedSeat>;
  return (
    typeof seat.code === 'string' &&
    seat.code.length > 0 &&
    typeof seat.rejoinToken === 'string' &&
    seat.rejoinToken.length > 0
  );
}

/**
 * Everything this device has a seat for, newest first.
 *
 * Folds in the old single-slot key on the way, so the update does not cost
 * anybody the game they are in the middle of. A phone that has been away for
 * months and comes back mid-round is exactly the case that must not break.
 */
export function savedSeats(): SavedSeat[] {
  const seats: SavedSeat[] = [];

  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as unknown;
      if (Array.isArray(parsed)) {
        for (const entry of parsed) if (isSeat(entry)) seats.push(normalise(entry));
      }
    }
  } catch {
    // Unreadable storage is the same as none: better an empty list than a
    // start-up that throws on the home screen.
  }

  try {
    const legacy = localStorage.getItem(LEGACY_KEY);
    if (legacy) {
      const parsed = JSON.parse(legacy) as unknown;
      if (isSeat(parsed) && !seats.some((seat) => seat.code === parsed.code.toUpperCase())) {
        /*
         * The old format has no mode. Every game reads its own state and refuses a
         * snapshot from the wrong one, so guessing wrong is a wrong link rather
         * than a wrong game — and 'live' is what almost every stored seat was.
         */
        seats.push(normalise({ ...parsed, mode: parsed.mode ?? 'live' }));
      }
    }
  } catch {
    // Same again.
  }

  return seats.sort((a, b) => b.savedAt.localeCompare(a.savedAt));
}

function normalise(seat: SavedSeat): SavedSeat {
  return {
    ...seat,
    code: seat.code.toUpperCase(),
    mode: seat.mode ?? 'live',
    name: seat.name ?? '',
    savedAt: seat.savedAt ?? new Date(0).toISOString(),
  };
}

function write(seats: SavedSeat[]): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(seats));
    // Only once the new list has actually been written: a phone that fails here
    // keeps the seat it had.
    localStorage.removeItem(LEGACY_KEY);
  } catch {
    // Storage full or blocked. Nothing to do but keep playing.
  }
}

/** The seat this device holds at that table, if any. */
export function seatFor(code: string): SavedSeat | null {
  const wanted = code.toUpperCase();
  return savedSeats().find((seat) => seat.code === wanted) ?? null;
}

/**
 * Remember a seat, replacing an earlier one at the same table.
 *
 * Called on every join and rejoin, so `savedAt` doubles as "when was I last at
 * this table" — which is the order the home screen wants.
 */
export function rememberSeat(seat: Omit<SavedSeat, 'savedAt'>): void {
  const rest = savedSeats().filter((held) => held.code !== seat.code.toUpperCase());
  write([normalise({ ...seat, savedAt: new Date().toISOString() }), ...rest].slice(0, 12));
}

/**
 * Forget a seat.
 *
 * Only for a table that is really gone — the server says 404 — or a game that
 * has ended. Never on leaving a screen: that is what took your chair away
 * before, and a chair you cannot return to is worse than a stale entry.
 */
export function forgetSeat(code: string): void {
  const wanted = code.toUpperCase();
  write(savedSeats().filter((seat) => seat.code !== wanted));
}
